import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useProperty } from '../../context/PropertyContext';
import toast from 'react-hot-toast';

const AdminStats = ({ properties, projects = [], onNavigate }) => {
  const { formatPrice, getAnalytics } = useProperty();
  const analytics = getAnalytics();
  const [galleryCount, setGalleryCount] = useState(0);

  // Load gallery count
  useEffect(() => {
    const loadGalleryCount = () => {
      const savedGallery = localStorage.getItem('truehome_gallery');
      if (savedGallery) {
        try {
          const gallery = JSON.parse(savedGallery);
          setGalleryCount(gallery.filter(img => img.isActive !== false).length);
        } catch (error) {
          setGalleryCount(0);
        }
      }
    };
    loadGalleryCount();
  }, []);

  const stats = [
    {
      title: 'Total Properties',
      value: analytics.totalProperties,
      icon: 'fas fa-home',
      color: 'primary',
      change: '+12%'
    },
    {
      title: 'Properties Sold',
      value: analytics.soldProperties,
      icon: 'fas fa-handshake',
      color: 'success',
      change: '+8%'
    },
    {
      title: 'Total Projects',
      value: analytics.totalProjects,
      icon: 'fas fa-building',
      color: 'warning',
      change: '+5%'
    },
    {
      title: 'Gallery Photos',
      value: galleryCount,
      icon: 'fas fa-images',
      color: 'info',
      change: 'Active'
    }
  ];

  const recentProperties = properties.slice(0, 3);
  const recentProjects = projects.slice(0, 3);

  const handleExportReport = () => {
    const reportData = {
      generatedAt: new Date().toISOString(),
      summary: {
        totalProperties: analytics.totalProperties,
        soldProperties: analytics.soldProperties,
        totalProjects: analytics.totalProjects,
        totalProjectValue: analytics.totalProjectValue,
        conversionRate: analytics.conversionRate
      },
      properties: properties.map(p => ({
        id: p.id,
        title: p.title,
        location: p.location,
        price: p.price,
        status: p.status,
        type: p.type
      })),
      projects: projects.map(p => ({
        id: p.id,
        title: p.title,
        location: p.location,
        status: p.status,
        type: p.type
      }))
    };

    const dataStr = JSON.stringify(reportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `TrueHome_Report_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Report exported successfully!');
  };

  return (
    <div className="admin-stats">
      <div className="stats-grid">
        {stats.map((stat, index) => (
          <motion.div
            key={index}
            className={`stat-card ${stat.color}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.3 }}
          >
            <div className="stat-icon">
              <i className={stat.icon}></i>
            </div>
            <div className="stat-content">
              <h3>{stat.value}</h3>
              <p>{stat.title}</p>
              <span className="stat-change">{stat.change}</span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Additional Analytics Cards */}
      <div className="analytics-row">
        <motion.div
          className="analytics-card"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, duration: 0.3 }}
        >
          <div className="analytics-header">
            <h3>Conversion Rate</h3>
            <span className="analytics-value">{analytics.conversionRate}%</span>
          </div>
          <div className="analytics-bar">
            <div 
              className="analytics-progress"
              style={{ width: `${analytics.conversionRate}%` }}
            ></div>
          </div>
        </motion.div>

        <motion.div
          className="analytics-card"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6, duration: 0.3 }}
        >
          <div className="analytics-header">
            <h3>Project Status</h3>
          </div>
          <div className="project-status">
            <div className="status-item">
              <span className="status-label">Completed</span>
              <span className="status-count">{analytics.completedProjects}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Ongoing</span>
              <span className="status-count">{analytics.ongoingProjects}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Planning</span>
              <span className="status-count">{analytics.planningProjects}</span>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="dashboard-widgets">
        <motion.div
          className="widget recent-properties"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4, duration: 0.3 }}
        >
          <div className="widget-header">
            <h3>Recent Properties</h3>
            <button className="widget-action">View All</button>
          </div>
          <div className="widget-content">
            {recentProperties.length > 0 ? (
              recentProperties.map((property) => (
                <div key={property.id} className="recent-property-item">
                  <img src={property.images[0]} alt={property.title} />
                  <div className="property-info">
                    <h4>{property.title}</h4>
                    <p>{property.location}</p>
                    <span className="property-price">{formatPrice(property.price)}</span>
                  </div>
                  <div className={`property-status ${property.status.toLowerCase().replace(' ', '-')}`}>
                    {property.status}
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <i className="fas fa-home"></i>
                <p>No properties added yet</p>
              </div>
            )}
          </div>
        </motion.div>

        <motion.div
          className="widget recent-projects"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, duration: 0.3 }}
        >
          <div className="widget-header">
            <h3>Recent Projects</h3>
            <button className="widget-action">View All</button>
          </div>
          <div className="widget-content">
            {recentProjects.length > 0 ? (
              recentProjects.map((project) => (
                <div key={project.id} className="recent-property-item">
                  <img src={project.image} alt={project.title} />
                  <div className="property-info">
                    <h4>{project.title}</h4>
                    <p>{project.location}</p>
                    <span className="property-price">{formatPrice(project.totalValue || 0)}</span>
                  </div>
                  <div className={`property-status ${project.status.toLowerCase().replace(' ', '-')}`}>
                    {project.status}
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <i className="fas fa-building"></i>
                <p>No projects added yet</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      <div className="dashboard-widgets">
        <motion.div
          className="widget property-types"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6, duration: 0.3 }}
        >
          <div className="widget-header">
            <h3>Property Types</h3>
          </div>
          <div className="widget-content">
            {Object.keys(analytics.propertyTypes).length > 0 ? (
              Object.entries(analytics.propertyTypes).map(([type, count]) => (
                <div key={type} className="property-type-item">
                  <div className="type-info">
                    <span className="type-name">{type}</span>
                    <span className="type-count">{count} properties</span>
                  </div>
                  <div className="type-bar">
                    <div 
                      className="type-progress"
                      style={{ 
                        width: `${(count / analytics.totalProperties) * 100}%` 
                      }}
                    ></div>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <i className="fas fa-chart-pie"></i>
                <p>No data available</p>
              </div>
            )}
          </div>
        </motion.div>

        <motion.div
          className="widget quick-actions"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.7, duration: 0.3 }}
        >
          <div className="widget-header">
            <h3>Quick Actions</h3>
          </div>
          <div className="widget-content">
            <div className="quick-actions-grid">
              <button 
                className="quick-action-btn"
                onClick={() => onNavigate && onNavigate('dashboard')}
              >
                <i className="fas fa-chart-pie"></i>
                <span>Dashboard</span>
              </button>
              <button 
                className="quick-action-btn"
                onClick={() => onNavigate && onNavigate('properties')}
              >
                <i className="fas fa-home"></i>
                <span>Properties</span>
              </button>
              <button 
                className="quick-action-btn"
                onClick={() => onNavigate && onNavigate('add-property')}
              >
                <i className="fas fa-plus"></i>
                <span>Add Property</span>
              </button>
              <button 
                className="quick-action-btn"
                onClick={() => onNavigate && onNavigate('projects')}
              >
                <i className="fas fa-building"></i>
                <span>Projects</span>
              </button>
              <button 
                className="quick-action-btn"
                onClick={() => onNavigate && onNavigate('add-project')}
              >
                <i className="fas fa-plus-circle"></i>
                <span>Add Project</span>
              </button>
              <button 
                className="quick-action-btn"
                onClick={() => onNavigate && onNavigate('gallery')}
              >
                <i className="fas fa-images"></i>
                <span>Gallery</span>
              </button>
              <button 
                className="quick-action-btn"
                onClick={() => onNavigate && onNavigate('theme')}
              >
                <i className="fas fa-palette"></i>
                <span>Theme</span>
              </button>
              <button 
                className="quick-action-btn"
                onClick={() => onNavigate && onNavigate('analytics')}
              >
                <i className="fas fa-chart-bar"></i>
                <span>Analytics</span>
              </button>
              <button 
                className="quick-action-btn"
                onClick={handleExportReport}
              >
                <i className="fas fa-download"></i>
                <span>Export Report</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminStats;